import React, { useState } from 'react';
import {
  Calendar,
  Clock,
  MessageCircle,
  Paperclip,
  Trash2,
  Link2,
  Timer,
  FileText,
  Send,
} from 'lucide-react';
import { format, parseISO, isBefore, differenceInDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import Modal from '../../components/WhatsAppChat/WaModal';
import Avatar from '../../components/WhatsAppChat/WaAvatar';
import { TICKET_CATEGORIES, PRIORITIES, KANBAN_COLUMNS } from '../../services/whatsapp/waConstants';
import type { KanbanTicket } from './KanbanCard';

interface TicketDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticket: KanbanTicket;
  onUpdate: (updated: KanbanTicket) => void;
  onDelete: (id: string) => void;
  onMove: (id: string, newColumnId: string) => void;
}

interface Comment {
  id: string;
  author: string;
  text: string;
  time: string;
}

interface ChecklistItem {
  id: string;
  text: string;
  done: boolean;
}

export default function TicketDetailModal({
  isOpen,
  onClose,
  ticket,
  onUpdate,
  onDelete,
  onMove,
}: TicketDetailModalProps) {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(ticket.title);
  const [description, setDescription] = useState(ticket.description || '');
  const [comment, setComment] = useState('');
  const [comments, setComments] = useState<Comment[]>([]);
  const [checklist, setChecklist] = useState<ChecklistItem[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'activity' | 'comments'>('details');

  const category = TICKET_CATEGORIES[ticket.category] || TICKET_CATEGORIES.outros;
  const priority = PRIORITIES[ticket.priority] || PRIORITIES.medium;
  const currentColumn = KANBAN_COLUMNS.find(c => c.id === ticket.columnId);
  const isOverdue = ticket.dueDate ? isBefore(parseISO(ticket.dueDate), new Date()) : false;
  const daysUntilDue = ticket.dueDate
    ? differenceInDays(parseISO(ticket.dueDate), new Date())
    : null;

  const handleSave = () => {
    onUpdate({ ...ticket, title, description });
    setEditing(false);
  };

  const handleAddComment = () => {
    if (!comment.trim()) return;
    const newComment: Comment = {
      id: `c-${Date.now()}`,
      author: 'Admin',
      text: comment.trim(),
      time: 'Agora',
    };
    setComments(prev => [...prev, newComment]);
    setComment('');
  };

  const priorityDotColors: Record<string, string> = {
    low: 'bg-[#8E8E93]',
    medium: 'bg-[#007AFF]',
    high: 'bg-[#FF9500]',
    urgent: 'bg-[#FF2D55] animate-pulse',
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="" size="xl">
      <div className="-mt-2 flex flex-col gap-6 lg:flex-row">
        {/* Left side - Main content */}
        <div className="min-w-0 flex-1">
          {/* Category & Priority header */}
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span
              className={`rounded-full px-2.5 py-1 text-xs font-semibold ${category.bg} ${category.color}`}
            >
              {category.label}
            </span>
            <span
              className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${priority.bg} ${priority.color}`}
            >
              <span className={`h-2 w-2 rounded-full ${priorityDotColors[ticket.priority]}`} />
              {priority.label}
            </span>
            {currentColumn && (
              <span className="rounded-full bg-gray-100/80 px-2.5 py-1 text-xs font-medium text-gray-500">
                {currentColumn.title}
              </span>
            )}
          </div>

          {/* Title */}
          {editing ? (
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              className="mb-2 w-full border-b-2 border-wa-500 bg-transparent pb-1 text-xl font-bold text-gray-900 outline-none"
              autoFocus // eslint-disable-line jsx-a11y/no-autofocus
            />
          ) : (
            <h2
              className="mb-2 cursor-pointer text-xl font-bold text-gray-900 transition-colors hover:text-wa-600"
              onClick={() => setEditing(true)}
              role="button" // eslint-disable-line jsx-a11y/no-noninteractive-element-to-interactive-role
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') setEditing(true);
              }}
            >
              {ticket.title}
            </h2>
          )}

          <p className="mb-4 text-sm text-gray-500">{ticket.client}</p>

          {/* Tabs - Segmented Control */}
          <div className="mb-4 flex gap-1 rounded-xl bg-gray-100/60 p-1">
            {[
              { key: 'details' as const, label: 'Detalhes', icon: FileText },
              { key: 'activity' as const, label: 'Atividade', icon: Clock },
              { key: 'comments' as const, label: 'Comentarios', icon: MessageCircle },
            ].map(tab => (
              <button
                type="button"
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={clsx(
                  'flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3.5 py-2 text-sm font-medium transition-all',
                  activeTab === tab.key
                    ? 'bg-white text-wa-600 shadow-wa-apple'
                    : 'text-gray-500 hover:text-gray-700',
                )}
              >
                <tab.icon size={14} />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Tab content */}
          {activeTab === 'details' && (
            <div>
              {/* Description */}
              <div className="mb-5">
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label
                  htmlFor="ticket-desc"
                  className="mb-1.5 block text-sm font-medium text-gray-700"
                >
                  Descricao
                </label>
                {editing ? (
                  <textarea
                    id="ticket-desc"
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    rows={4}
                    className="w-full resize-none rounded-xl border-0 bg-white px-4 py-3 text-sm shadow-wa-apple outline-none focus:ring-2 focus:ring-wa-500/50"
                    placeholder="Descreva a tarefa..."
                  />
                ) : (
                  <div
                    className="min-h-[80px] cursor-pointer rounded-xl bg-white p-4 text-sm text-gray-600 shadow-wa-apple transition-all hover:bg-white/80"
                    onClick={() => setEditing(true)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') setEditing(true);
                    }}
                  >
                    {description || 'Clique para adicionar uma descricao...'}
                  </div>
                )}
              </div>

              {editing && (
                <div className="mb-5 flex gap-2">
                  <button
                    type="button"
                    onClick={handleSave}
                    className="rounded-xl bg-wa-500 px-5 py-2 text-sm font-semibold text-white shadow-wa-apple transition-all hover:bg-wa-600"
                  >
                    Salvar
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditing(false);
                      setTitle(ticket.title);
                      setDescription(ticket.description || '');
                    }}
                    className="rounded-xl px-5 py-2 text-sm text-gray-500 transition-all hover:bg-gray-100"
                  >
                    Cancelar
                  </button>
                </div>
              )}

              {/* Checklist */}
              <div className="mb-5">
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label className="mb-2.5 block text-sm font-medium text-gray-700">Checklist</label>
                {checklist.length === 0 ? (
                  <p className="rounded-xl bg-white p-4 text-sm text-gray-400 shadow-wa-apple">
                    Nenhum item no checklist
                  </p>
                ) : (
                  <div className="space-y-2.5">
                    {checklist.map(item => (
                      // eslint-disable-next-line jsx-a11y/label-has-associated-control
                      <label
                        key={item.id}
                        className="group flex cursor-pointer items-center gap-3 rounded-xl p-2 text-sm transition-colors hover:bg-gray-50"
                      >
                        <input
                          type="checkbox"
                          checked={item.done}
                          onChange={() =>
                            setChecklist(prev =>
                              prev.map(c => (c.id === item.id ? { ...c, done: !c.done } : c)),
                            )
                          }
                          className="w-4.5 h-4.5 rounded-md border-gray-300 text-wa-500 focus:ring-wa-500"
                        />
                        <span
                          className={clsx(
                            item.done && 'text-gray-400 line-through',
                            !item.done && 'text-gray-700',
                          )}
                        >
                          {item.text}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
                {checklist.length > 0 && (
                  <>
                    <div className="mt-3 h-2 rounded-full bg-gray-200/60">
                      <div
                        className="h-2 rounded-full bg-gradient-to-r from-wa-400 to-wa-600 transition-all"
                        style={{
                          width: `${(checklist.filter(c => c.done).length / checklist.length) * 100}%`,
                        }}
                      />
                    </div>
                    <p className="mt-1.5 text-xs font-medium text-gray-400">
                      {checklist.filter(c => c.done).length} de {checklist.length} concluidos
                    </p>
                  </>
                )}
              </div>

              {/* Attachments placeholder */}
              <div>
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label className="mb-2 block text-sm font-medium text-gray-700">Anexos</label>
                <div className="cursor-pointer rounded-2xl border-2 border-dashed border-gray-200/60 bg-white/30 p-5 text-center transition-all hover:border-wa-400">
                  <Paperclip size={20} className="mx-auto mb-1.5 text-gray-400" />
                  <p className="text-xs text-gray-500">Arraste arquivos ou clique para anexar</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'activity' && (
            <div className="flex min-h-[120px] items-center justify-center rounded-xl bg-white p-6 shadow-wa-apple">
              <p className="text-sm text-gray-400">Nenhuma atividade registrada</p>
            </div>
          )}

          {activeTab === 'comments' && (
            <div>
              {/* Comments list */}
              <div className="mb-5 space-y-4">
                {comments.length === 0 ? (
                  <p className="rounded-xl bg-white p-4 text-sm text-gray-400 shadow-wa-apple">
                    Nenhum comentario ainda
                  </p>
                ) : (
                  comments.map(c => (
                    <div key={c.id} className="flex gap-3">
                      <Avatar name={c.author} size="sm" />
                      <div className="flex-1 rounded-2xl bg-white p-4 shadow-wa-apple">
                        <div className="mb-1.5 flex items-center justify-between">
                          <span className="text-sm font-semibold text-gray-900">{c.author}</span>
                          <span className="text-xs text-gray-400">{c.time}</span>
                        </div>
                        <p className="text-sm text-gray-600">{c.text}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* New comment */}
              <div className="flex gap-3">
                <Avatar name="Admin" size="sm" />
                <div className="relative flex-1">
                  <textarea
                    value={comment}
                    onChange={e => setComment(e.target.value)}
                    placeholder="Escreva um comentario..."
                    rows={2}
                    className="w-full resize-none rounded-xl border-0 bg-white px-4 py-3 pr-10 text-sm shadow-wa-apple outline-none focus:ring-2 focus:ring-wa-500/50"
                  />
                  <button
                    type="button"
                    onClick={handleAddComment}
                    className="absolute bottom-3 right-3 rounded-lg p-1.5 text-wa-500 transition-all hover:bg-wa-50 hover:text-wa-600"
                  >
                    <Send size={16} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right side - Meta info */}
        <div className="w-full flex-shrink-0 space-y-4 lg:w-56">
          {/* Status / Move */}
          <div>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label
              htmlFor="ticket-status"
              className="mb-2 block text-xs font-semibold uppercase tracking-wider text-gray-400"
            >
              Status
            </label>
            <select
              id="ticket-status"
              value={ticket.columnId}
              onChange={e => onMove(ticket.id, e.target.value)}
              className="w-full rounded-xl border-0 bg-white px-3.5 py-2.5 text-sm shadow-wa-apple outline-none focus:ring-2 focus:ring-wa-500/50"
            >
              {KANBAN_COLUMNS.map(col => (
                <option key={col.id} value={col.id}>
                  {col.title}
                </option>
              ))}
            </select>
          </div>

          {/* Assignee */}
          <div>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-gray-400">
              Responsavel
            </label>
            <div className="flex items-center gap-2.5 rounded-xl bg-white px-3.5 py-2.5 shadow-wa-apple">
              <Avatar name={ticket.assignee} size="sm" />
              <span className="text-sm font-medium text-gray-900">{ticket.assignee}</span>
            </div>
          </div>

          {/* Due date */}
          <div>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-gray-400">
              Prazo
            </label>
            {ticket.dueDate ? (
              <div
                className={clsx(
                  'flex items-center gap-2 rounded-xl px-3.5 py-2.5 text-sm font-medium',
                  // eslint-disable-next-line no-nested-ternary
                  isOverdue
                    ? 'bg-[#FF2D55]/10 text-[#FF2D55]'
                    : daysUntilDue !== null && daysUntilDue <= 3
                      ? 'bg-[#FF9500]/10 text-[#FF9500]'
                      : 'bg-white text-gray-700 shadow-wa-apple',
                )}
              >
                <Calendar size={14} />
                <span>
                  {format(parseISO(ticket.dueDate), "dd 'de' MMM, yyyy", { locale: ptBR })}
                </span>
                {isOverdue && <span className="ml-auto text-xs font-bold">Atrasado</span>}
                {!isOverdue && daysUntilDue !== null && daysUntilDue <= 3 && (
                  <span className="ml-auto text-xs font-bold">{daysUntilDue}d</span>
                )}
              </div>
            ) : (
              <p className="px-3.5 py-2.5 text-sm text-gray-400">Sem prazo definido</p>
            )}
          </div>

          {/* Time tracking */}
          <div>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-gray-400">
              Tempo
            </label>
            <div className="flex items-center gap-2 rounded-xl bg-white px-3.5 py-2.5 text-sm font-medium text-gray-700 shadow-wa-apple">
              <Timer size={14} />
              <span>{ticket.estimatedHours || 0}h estimadas</span>
            </div>
          </div>

          {/* Tags */}
          <div>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-gray-400">
              Tags
            </label>
            <div className="flex flex-wrap gap-1.5">
              <span className="rounded-full bg-wa-50 px-2.5 py-1 text-xs font-semibold text-wa-600">
                {category.label}
              </span>
              <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
                {ticket.client.split(' ')[0]}
              </span>
              <button
                type="button"
                className="rounded-full border border-dashed border-gray-300 px-2.5 py-1 text-xs text-gray-400 transition-colors hover:border-wa-400 hover:text-wa-500"
              >
                + Tag
              </button>
            </div>
          </div>

          {/* Conversation link */}
          <div>
            <button
              type="button"
              disabled={!ticket.conversationId}
              onClick={() => {
                if (ticket.conversationId) {
                  onClose();
                  navigate(`/whatsapp?conversation=${ticket.conversationId}`);
                }
              }}
              className="flex w-full items-center gap-2 rounded-xl bg-wa-50 px-3.5 py-2.5 text-sm font-medium text-wa-600 transition-all hover:bg-wa-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Link2 size={14} />
              Ver conversa no Chat
            </button>
          </div>

          {/* Delete */}
          <div className="border-t border-gray-200/60 pt-3">
            {showDeleteConfirm ? (
              <div className="space-y-2.5">
                <p className="text-xs font-medium text-[#FF2D55]">
                  Tem certeza? Essa acao nao pode ser desfeita.
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      onDelete(ticket.id);
                      onClose();
                    }}
                    className="flex-1 rounded-xl bg-[#FF2D55] px-3 py-2 text-xs font-semibold text-white transition-all hover:bg-[#FF2D55]/90"
                  >
                    Excluir
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(false)}
                    className="flex-1 rounded-xl px-3 py-2 text-xs font-medium text-gray-500 transition-all hover:bg-gray-100"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                className="flex w-full items-center gap-2 rounded-xl px-3.5 py-2.5 text-sm font-medium text-[#FF2D55] transition-all hover:bg-[#FF2D55]/5"
              >
                <Trash2 size={14} />
                Excluir ticket
              </button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
